import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  Box,
  AppBar,
  Toolbar,
  Drawer,
  IconButton,
  Typography,
  CircularProgress,
  Backdrop,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import {
  changeLayout,
  changeSidebarTheme,
  changeSidebarType,
} from "../store/actions"

import MuiHeader from "./MuiHeader"
import MuiSidebarContent from "./MuiSidebarContent"
import MuiFooter from "./MuiFooter"
import ChatPanel from "../components/mui/ChatPanel"

const MainLayout = ({ children }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [mobileOpen, setMobileOpen] = useState(false)

  const isPreloader = useSelector(state => state.Layout.isPreloader)
  const leftSideBarTheme = useSelector(state => state.Layout.leftSideBarTheme)

  // Read layout dimensions from theme (enterprise theme overrides these)
  const layoutConfig = theme.layoutConfig || {}
  const drawerWidth = layoutConfig.drawerWidth || 260
  const headerHeight = layoutConfig.headerHeight || 70
  const contentPadding = layoutConfig.contentPadding || 3
  const contentPaddingTop = layoutConfig.contentPaddingTop || 2
  const isEliton = !!theme.eliton
  const sidebarBg = isEliton ? "#212121" : "background.paper"

  useEffect(() => {
    dispatch(changeLayout("vertical"))
    dispatch(changeSidebarType("default"))
  }, [dispatch])

  useEffect(() => {
    if (leftSideBarTheme) {
      dispatch(changeSidebarTheme(leftSideBarTheme))
    }
  }, [leftSideBarTheme, dispatch])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 2 }}
        open={isPreloader}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      {/* Header */}
      <MuiHeader
        drawerWidth={drawerWidth}
        onMenuClick={handleDrawerToggle}
        isMobile={isMobile}
      />

      {/* Mobile Drawer - Only visible on small screens */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: sidebarBg,
          },
        }}
      >
        <MuiSidebarContent onItemClick={() => setMobileOpen(false)} />
      </Drawer>

      {/* Desktop Drawer - Always open on large screens */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: sidebarBg,
            borderRight: "none",
            boxShadow: isEliton ? "none" : "0 2px 4px rgba(0,0,0,0.08)",
          },
        }}
      >
        <MuiSidebarContent />
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: "background.default",
        }}
      >
        <Toolbar sx={{ minHeight: `${headerHeight}px !important`, flexShrink: 0 }} />
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: contentPadding,
            pt: contentPaddingTop,
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Chat Panel (triggered from header icon) */}
      <ChatPanel />
    </Box>
  )
}

export default MainLayout
